import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  setCurrentStep,
  setDifficultyLevel
} from '../../redux/Slices/InterviewSlice';
import { Language } from '../../redux/Slices/InterviewSlice/types';
import {
  setCurrentTime,
  setIsOnBreak
} from '../../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  useGetFileFromS3Mutation,
  useUploadFileToS3Mutation
} from '../../services/CloudApi';
import {
  useGetAvatarsQuery,
  useGetTopicQuery,
  useSubmitAnswerMutation
} from '../../services/interViewApi';
import HRRound from '../HrRound';
import InterviewInstructionsV2 from '../ReUsableComponents/InterviewInstructions/InterviewInstructionsV2';
import ModuleBreakTime from '../ReUsableComponents/SectionBreakTimer';

import ChooseLanguage from './ChooseLanguage';
import Interview from './Interview';
import InterviewComplete from './InterviewComplete';

const SpeakingInterviewApp: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [overallTimer, setOverallTimer] = useState(1800);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [getFileFromS3] = useGetFileFromS3Mutation();
  const [uploadFileToS3] = useUploadFileToS3Mutation();
  const [submitAnswer] = useSubmitAnswerMutation();
  const [answersApiStatus, setAnswerApiStatus] = useState({});
  const [isLastAnswerSubmited, setIsLastAnswerSubmited] = useState(false);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  useGetTopicQuery({}, { refetchOnMountOrArgChange: true });
  useGetAvatarsQuery({}, { refetchOnMountOrArgChange: true });

  const { testData, currentStep } = useAppSelector(
    state => state.root.interview
  );

  const { isDriveTest, nextRound, isOnBreak } = useAppSelector(
    state => state.root.startFulltest
  );

  const audioRef = useRef<HTMLAudioElement>(null);

  // Initialize media recorder
  useEffect(() => {
    const initMediaRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        const recorder = new MediaRecorder(stream);

        recorder.ondataavailable = event => {
          if (event.data.size > 0) {
            const audioBlob = new Blob([event.data], {
              type: 'audio/webm'
            });
            const audioFile = new File([audioBlob], 'recording.webm', {
              type: 'audio/webm'
            });
            uploadToS3(audioFile);
            setRecordedChunks(prev => [...prev, event.data]);
          }
        };

        setMediaRecorder(recorder);
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    };

    if (currentStep === 'interview') {
      initMediaRecorder();
    }
  }, [currentStep, currentQuestionIndex]);

  const startRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === 'inactive') {
      setRecordedChunks([]);
      mediaRecorder.start();
      setIsRecording(true);
    } else {
      console.warn(
        'Unable to start recording: MediaRecorder is not active',
        mediaRecorder
      );
    }
  }, [mediaRecorder]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
    } else {
      console.warn('Unable to stop recording', mediaRecorder);
    }
  }, [mediaRecorder]);

  const uploadToS3 = async (file: File) => {
    const questionid = testData?.testSet?.questions[currentQuestionIndex]?.id;
    try {
      const response = await uploadFileToS3(file).unwrap();
      const payload = {
        questionId: testData?.testSet?.questions[currentQuestionIndex]?.id,
        uploadedAnswerFile: {
          ...response.data,
          originalFileName: file.name
        }
      };
      setAnswerApiStatus(prev => ({
        ...prev,
        [payload.questionId]: 'pending'
      }));
      const res = await submitAnswer({
        userTestId: testData?.id,
        body: payload
      }).unwrap();
      if (res.status === 'success') {
        setAnswerApiStatus(prev => ({
          ...prev,
          [payload.questionId]: 'success'
        }));
      }
      if (
        currentQuestionIndex ===
        testData?.testSet?.questions.length - 1
        // res.status === 'success'
      ) {
        // dispatch(setCurrentStep('complete'));
      }
    } catch (err) {
      setIsLoading(false);
      setAnswerApiStatus(prev => ({
        ...prev,
        [questionid]: 'error'
      }));
      console.error('Upload failed:', err);
    }
  };

  const playQuestion = useCallback(async () => {
    const currentQuestion = testData?.testSet?.questions[currentQuestionIndex];
    let url = '';
    if (currentQuestion?.audioFile?.fileName) {
      url = await getFileFromS3(currentQuestion?.audioFile?.fileName).unwrap();
    }
    if (audioRef.current && url) {
      setIsPlayingAudio(true);

      // Set up audio element
      audioRef.current.src = url;
      audioRef.current.load();

      // Handle audio end event
      audioRef.current.onended = () => {
        setIsPlayingAudio(false);
        setShowTimer(true);
        startRecording();
      };

      // Handle audio error
      audioRef.current.onerror = () => {
        setIsPlayingAudio(false);
        setShowTimer(true);
        startRecording();
      };

      try {
        await audioRef.current.play();
      } catch (error) {
        console.error('Error playing audio:', error);
        setIsPlayingAudio(false);
        setShowTimer(true);
        startRecording();
      }
    }
  }, [startRecording, currentQuestionIndex, testData]);

  const handleNextQuestion = useCallback(async () => {
    stopRecording();
    setShowTimer(false);
    const isLastQuestion =
      currentQuestionIndex >= testData?.testSet?.questions.length - 1;
    if (!isLastQuestion && overallTimer > 0) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setIsRecording(false);
      setIsPlayingAudio(false);
      setIsLoading(true);
      setIsLastAnswerSubmited(true);
      // setTimeout(() => {
      //   dispatch(setCurrentStep('complete'));
      // }, 4000);
      if (isDriveTest) {
        dispatch(setIsOnBreak(true));
        const sectionBreakTime = 300;
        dispatch(setCurrentTime(sectionBreakTime));
        // dispatch(setCurrentStep('instructions'));
        // dispatch(setNextRound('hr'));
      }
    }
  }, [currentQuestionIndex, stopRecording, testData?.id, overallTimer]);

  const handleTimerComplete = useCallback(() => {
    handleNextQuestion();
  }, [handleNextQuestion]);

  const checkMicrophonePermission = async () => {
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia is not supported');
        window.alert(
          'Your browser does not support camera or microphone access. Please use a modern browser like Chrome, Firefox, or Edge.'
        );
        return;
      }
      // Try to get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      // If successful, we have permission
      dispatch(setCurrentStep('language'));
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.error('Microphone permission denied:', error);
      window.alert(
        'Microphone permission is required to start the test. Please allow microphone access in your browser settings.'
      );
    }
  };

  useEffect(() => {
    if (
      currentStep === 'interview' &&
      !showTimer &&
      !isPlayingAudio &&
      mediaRecorder
    ) {
      const timer = setTimeout(() => {
        playQuestion();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    currentQuestionIndex,
    currentStep,
    showTimer,
    isPlayingAudio,
    playQuestion,
    mediaRecorder
  ]);

  useEffect(() => {
    const isLastQuestion =
      currentQuestionIndex >= testData?.testSet?.questions.length - 1;
    if (isLastAnswerSubmited && isLastQuestion && !isDriveTest) {
      const statuses = Object.values(answersApiStatus).every(
        each => each !== 'pending'
      );
      if (statuses) {
        dispatch(setCurrentStep('complete'));
        setIsLastAnswerSubmited(false);
        setAnswerApiStatus({});
      }
    } else if (isLastAnswerSubmited && isLastQuestion && isDriveTest) {
      dispatch(setIsOnBreak(true));
      const sectionBreakTime = 300;
      dispatch(setCurrentTime(sectionBreakTime));
      // dispatch(setNextRound('hr'));
      // dispatch(setCurrentStep('instructions'));
    }
  }, [answersApiStatus]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentStep === 'interview') {
        const message = 'Changes you made may not be saved.';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep]);

  useEffect(() => {
    const handleBackButton = (event: any) => {
      if (currentStep === 'interview') {
        const confirmBack = window.confirm(
          'Are you sure you want to leave this page? Any changes you have made will be lost, and youll need to start the test over from the beginning.'
        );
        if (!confirmBack) {
          event.preventDefault();
        } else {
          dispatch(setCurrentStep('start'));
          setCurrentQuestionIndex(0);
          setSelectedLanguage(null);
          setShowTimer(false);
          setIsRecording(false);
          setIsPlayingAudio(false);
          setOverallTimer(1800);
        }
      }
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [location, navigate, currentStep]);

  useEffect(() => {
    if (!isDriveTest) {
      dispatch(setCurrentStep('language'));
      dispatch(setDifficultyLevel(''));
    }
  }, [isDriveTest]);

  if (currentStep === 'start') {
    return (
      <div className='flex w-full justify-center'>
        <button
          className='mx-auto mt-8 rounded-xl bg-primary px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-green-600 hover:to-blue-700'
          onClick={() => checkMicrophonePermission()}
        >
          Start Test
        </button>
      </div>
    );
  }

  if (currentStep === 'language') {
    return (
      <ChooseLanguage
        setSelectedLanguage={setSelectedLanguage}
        selectedLanguage={selectedLanguage}
      />
    );
  }

  if (isOnBreak) {
    return <ModuleBreakTime nextR='hr' />;
  }

  if (nextRound === 'hr') {
    return <HRRound />;
  }

  if (currentStep === 'instructions') {
    return <InterviewInstructionsV2 isHr={false} />;
  }

  if (currentStep === 'interview') {
    const currentQuestion =
      testData?.testSet?.questions?.[currentQuestionIndex];

    return (
      <Interview
        currentQuestionIndex={currentQuestionIndex}
        currentQuestion={currentQuestion}
        isPlayingAudio={isPlayingAudio}
        showTimer={showTimer}
        isRecording={isRecording}
        handleTimerComplete={handleTimerComplete}
        handleNextQuestion={handleNextQuestion}
        audioRef={audioRef}
        overallTimer={overallTimer}
        setOverallTimer={setOverallTimer}
        loading={isLoading}
      />
    );
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 to-blue-100 p-4'>
      <InterviewComplete
        setCurrentQuestionIndex={setCurrentQuestionIndex}
        setSelectedLanguage={setSelectedLanguage}
        setShowTimer={setShowTimer}
        setIsRecording={setIsRecording}
        setIsPlayingAudio={setIsPlayingAudio}
        setOverallTimer={setOverallTimer}
        loading={isLoading}
        setIsLoading={setIsLoading}
      />
    </div>
  );
};

export default SpeakingInterviewApp;
